import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  IconButton,
  Pagination,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import MailIcon from "@mui/icons-material/Mail";
import EditIcon from "@mui/icons-material/Edit";
import { useTranslation } from "react-i18next";
import { useLoginStatus } from "../../../../context/LoginStatusContext";
import { apiRequest } from "../../../../utils/api";
import { useAlert } from "../../../../context/AlertContext";
import InviteDialog from "./InviteDialog";
import DeleteMemberDialog from "./DeleteMemberDialog";
import EditMemberDialog from "./EditMemberDialog";
import PermissionMatrix from "./PermissionMatrix";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`member-tabpanel-${index}`}
      aria-labelledby={`member-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
    </div>
  );
}

interface Member {
  uuid: string;
  user: {
    uuid: number;
    username: string;
    email: string;
  };
  role: string;
  status: string;
  joined_at: string;
}

interface Invitation {
  uuid: string;
  email: string;
  role: string;
  expires_at: string;
  created_at: string;
  accepted: boolean;
}

const MemberManagement = () => {
  const { t } = useTranslation();
  const { user } = useLoginStatus();
  const [tabValue, setTabValue] = useState(0);
  const [members, setMembers] = useState<Member[]>([]);
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [openInviteDialog, setOpenInviteDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openEditRoleDialog, setOpenEditRoleDialog] = useState(false);
  const [selectedMember, setSelectedMember] = useState<Member | null>(null);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("member");
  const [editRole, setEditRole] = useState("");
  const [error, setError] = useState("");
  const [totalMemberCount, setTotalMemberCount] = useState(0);
  const { setAlert } = useAlert();

  // メンバーリストのページネーション用の状態
  const [memberCurrentPage, setMemberCurrentPage] = useState(1);
  const [memberTotalPages, setMemberTotalPages] = useState(1);
  const memberPageSize = 10; // 固定値として定義

  // 招待リストのページネーション用の状態
  const [invitationCurrentPage, setInvitationCurrentPage] = useState(1);
  const [invitationTotalPages, setInvitationTotalPages] = useState(1);
  const invitationPageSize = 10; // 固定値として定義

  useEffect(() => {
    if (error) {
      setAlert("error", error);
    }
  }, [error]);

  const organizationUuid = user?.enterprise?.organization?.uuid;
  const userRole = user?.enterprise?.role;
  const permissions = user?.enterprise?.permissions || [];

  const canInviteMembers = userRole === "owner" || userRole === "manager" || permissions.includes("invite_members");
  const canRemoveMembers = userRole === "owner" || userRole === "manager" || permissions.includes("remove_members");
  const canManageManagers = userRole === "owner" || permissions.includes("manage_managers");

  // タブ変更時には対応するデータを取得
  useEffect(() => {
    if (organizationUuid) {
      if (tabValue === 0) {
        fetchMembers();
      } else if (tabValue === 1) {
        fetchInvitations();
      }
    }
  }, [organizationUuid, tabValue, memberCurrentPage, memberPageSize, invitationCurrentPage, invitationPageSize]);

  const fetchMembers = async () => {
    try {
      // ページネーションパラメータを追加
      const params = new URLSearchParams();
      params.append("page", memberCurrentPage.toString());
      params.append("page_size", memberPageSize.toString());

      const response = await apiRequest(
        `/api/v1/enterprise/organizations/${organizationUuid}/members/?${params.toString()}`,
        "GET"
      );

      // ページネーション対応レスポンスの処理
      if (response.data.results) {
        // データ更新
        setMembers(response.data.results);
        setMemberTotalPages(Math.ceil(response.data.count / memberPageSize));
        setTotalMemberCount(response.data.count);
      } else {
        // 後方互換性のために古い形式もサポート
        setMembers(response.data);
        setTotalMemberCount(response.data.length);
      }
    } catch (error) {
      console.error("メンバー取得エラー:", error);
      setError(t("preferences.enterprise.error.fetchMembers"));
    }
  };

  const fetchInvitations = async () => {
    try {
      // ページネーションパラメータを追加
      const params = new URLSearchParams();
      params.append("page", invitationCurrentPage.toString());
      params.append("page_size", invitationPageSize.toString());

      const response = await apiRequest(
        `/api/v1/enterprise/organizations/${organizationUuid}/invitations/?${params.toString()}`,
        "GET"
      );

      // ページネーション対応レスポンスの処理
      if (response.data.results) {
        setInvitations(response.data.results);
        setInvitationTotalPages(Math.ceil(response.data.count / invitationPageSize));
      } else {
        // 後方互換性のために古い形式もサポート
        setInvitations(response.data);
      }
    } catch (error) {
      console.error("招待取得エラー:", error);
      setError(t("preferences.enterprise.error.fetchInvitations"));
    }
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleInviteOpen = () => {
    setInviteEmail("");
    setInviteRole("member");
    setOpenInviteDialog(true);
  };

  const handleInviteClose = () => {
    setOpenInviteDialog(false);
  };

  const handleDeleteMemberOpen = (member: Member) => {
    setSelectedMember(member);
    setOpenDeleteDialog(true);
  };

  const handleDeleteMemberClose = () => {
    setOpenDeleteDialog(false);
    setSelectedMember(null);
  };

  const handleEditRoleOpen = (member: Member) => {
    setSelectedMember(member);
    setEditRole(member.role);
    setOpenEditRoleDialog(true);
  };

  const handleEditRoleClose = () => {
    setOpenEditRoleDialog(false);
    setSelectedMember(null);
  };

  const handleUpdateMember = async () => {
    if (!selectedMember) return;

    try {
      // ロールとステータスの変更を一括で送信
      const updateData: Record<string, string> = {};

      // ロールの変更があれば追加
      if (editRole !== selectedMember.role) {
        updateData.role = editRole;
      }

      // ステータスの変更があれば追加
      const originalStatus = members.find((m) => m.uuid === selectedMember.uuid)?.status || "active";
      const newStatus = selectedMember.status;

      if (originalStatus !== newStatus) {
        updateData.status = newStatus;
      }

      // 変更がある場合のみAPIを呼び出し
      if (Object.keys(updateData).length > 0) {
        await apiRequest(
          `/api/v1/enterprise/organizations/${organizationUuid}/members/${selectedMember.uuid}/`,
          "PATCH",
          updateData
        );
        setAlert("success", t("preferences.enterprise.success.updateMember"));
      }

      setOpenEditRoleDialog(false);
      setSelectedMember(null);
      setError("");
      fetchMembers();
    } catch (error) {
      console.error("メンバー更新エラー:", error);
      setError(t("preferences.enterprise.error.updateMember"));
    }
  };

  const handleInviteMember = async () => {
    if (!inviteEmail) {
      setError(t("preferences.enterprise.error.emailRequired"));
      return;
    }

    try {
      await apiRequest(`/api/v1/enterprise/organizations/${organizationUuid}/invitations/`, "POST", {
        email: inviteEmail,
        role: inviteRole,
      });

      setOpenInviteDialog(false);
      fetchInvitations();
      setAlert("success", t("preferences.enterprise.inviteSuccess"));
    } catch (error) {
      console.error("招待エラー:", error);
      setError(t("preferences.enterprise.error.inviteMember"));
    }
  };

  const handleDeleteMember = async () => {
    if (!selectedMember) return;

    try {
      await apiRequest(
        `/api/v1/enterprise/organizations/${organizationUuid}/members/${selectedMember.uuid}/`,
        "DELETE"
      );

      setOpenDeleteDialog(false);
      setSelectedMember(null);
      fetchMembers();
      setAlert("success", t("preferences.enterprise.success.deleteMember"));
    } catch (error) {
      console.error("メンバー削除エラー:", error);
      setError(t("preferences.enterprise.error.deleteMember"));
    }
  };

  const handleResendInvitation = async (invitation: Invitation) => {
    try {
      await apiRequest(
        `/api/v1/enterprise/organizations/${organizationUuid}/invitations/${invitation.uuid}/resend/`,
        "POST"
      );

      fetchInvitations();
      setAlert("success", t("preferences.enterprise.success.resendInvitation"));
    } catch (error) {
      console.error("招待再送信エラー:", error);
      setError(t("preferences.enterprise.error.resendInvitation"));
    }
  };

  const handleCancelInvitation = async (invitation: Invitation) => {
    try {
      await apiRequest(
        `/api/v1/enterprise/organizations/${organizationUuid}/invitations/${invitation.uuid}/`,
        "DELETE"
      );

      fetchInvitations();
      setAlert("success", t("preferences.enterprise.success.cancelInvitation"));
    } catch (error) {
      console.error("招待キャンセルエラー:", error);
      setError(t("preferences.enterprise.error.cancelInvitation"));
    }
  };

  if (!user?.enterprise) {
    return <Typography>{t("preferences.enterprise.noEnterpriseInfo")}</Typography>;
  }

  // ローディング表示を削除し、常にテーブルを表示する
  // ローディング中はテーブルをそのまま表示し、データを維持することでUIが安定する

  const getRoleTranslation = (role: string) => {
    if (role === "owner") return t("preferences.enterprise.role.owner");
    if (role === "manager") return t("preferences.enterprise.role.manager");
    return t("preferences.enterprise.role.member");
  };

  const getStatusTranslation = (status: string) => {
    if (status === "active") return t("preferences.enterprise.active");
    if (status === "invited") return t("preferences.enterprise.invited");
    if (status === "suspended") return t("preferences.enterprise.suspended");
    if (status === "pending") return t("preferences.enterprise.pending");
    if (status === "expired") return t("preferences.enterprise.expired");
    return status;
  };

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography variant="subtitle1">{totalMemberCount} members</Typography>
        {canInviteMembers && (
          <Button variant="contained" color="primary" startIcon={<PersonAddIcon />} onClick={handleInviteOpen}>
            {t("preferences.enterprise.inviteMember")}
          </Button>
        )}
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label={t("preferences.enterprise.memberList")} />
          <Tab label={t("preferences.enterprise.invitationList")} />
          <Tab label={t("preferences.enterprise.permissionMatrix")} />
        </Tabs>
      </Box>

      <TabPanel value={tabValue} index={0}>
        <TableContainer component={Paper} sx={{ minHeight: "530px" }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t("preferences.enterprise.member.name")}</TableCell>
                <TableCell>{t("preferences.enterprise.member.email")}</TableCell>
                <TableCell>{t("preferences.enterprise.member.role")}</TableCell>
                <TableCell>{t("preferences.enterprise.member.status")}</TableCell>
                <TableCell>{t("preferences.enterprise.member.joinedAt")}</TableCell>
                {canRemoveMembers && <TableCell align="right">{t("preferences.enterprise.action")}</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {members.map((member) => (
                <TableRow key={member.uuid}>
                  <TableCell>{member.user.username}</TableCell>
                  <TableCell>{member.user.email}</TableCell>
                  <TableCell>{getRoleTranslation(member.role)}</TableCell>
                  <TableCell>{getStatusTranslation(member.status)}</TableCell>
                  <TableCell>{new Date(member.joined_at).toLocaleDateString()}</TableCell>
                  {canRemoveMembers && (
                    <TableCell align="right">
                      <>
                        {/* ロール編集ボタン */}
                        {(canManageManagers || (member.role === "member" && userRole === "manager")) && (
                          <Tooltip
                            title={
                              member.user.uuid === user.uuid
                                ? t("preferences.enterprise.selfEditDisabled")
                                : t("preferences.enterprise.member.editRole")
                            }
                          >
                            <span>
                              <IconButton
                                color="primary"
                                onClick={() => handleEditRoleOpen(member)}
                                disabled={
                                  member.user.uuid === user.uuid ||
                                  ((member.role === "owner" || member.role === "manager") && !canManageManagers)
                                }
                                sx={{ mr: 1 }}
                                size="small"
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}

                        {/* 削除ボタン */}
                        <Tooltip
                          title={
                            member.user.uuid === user.uuid
                              ? t("preferences.enterprise.selfDeleteDisabled")
                              : t("preferences.enterprise.deleteMember")
                          }
                        >
                          <span>
                            <IconButton
                              color="error"
                              onClick={() => handleDeleteMemberOpen(member)}
                              disabled={
                                member.user.uuid === user.uuid || (member.role === "owner" && !canManageManagers)
                              }
                              size="small"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* メンバーリストのページネーションコントロール */}
        {memberTotalPages > 1 && (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
            <Pagination
              count={memberTotalPages}
              page={memberCurrentPage}
              onChange={(event, value) => setMemberCurrentPage(value)}
              color="primary"
            />
          </Box>
        )}
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <TableContainer component={Paper} sx={{ minHeight: "530px" }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t("preferences.enterprise.invitation.email")}</TableCell>
                <TableCell>{t("preferences.enterprise.invitation.role")}</TableCell>
                <TableCell>{t("preferences.enterprise.invitation.sentAt")}</TableCell>
                <TableCell>{t("preferences.enterprise.invitation.expiresAt")}</TableCell>
                <TableCell>{t("preferences.enterprise.invitation.status")}</TableCell>
                {canInviteMembers && <TableCell align="right">{t("preferences.enterprise.action")}</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {invitations.map((invitation) => (
                <TableRow key={invitation.uuid}>
                  <TableCell>{invitation.email}</TableCell>
                  <TableCell>{getRoleTranslation(invitation.role)}</TableCell>
                  <TableCell>{new Date(invitation.created_at).toLocaleDateString()}</TableCell>
                  <TableCell>{new Date(invitation.expires_at).toLocaleDateString()}</TableCell>
                  <TableCell>
                    {invitation.accepted
                      ? t("preferences.enterprise.invitation.accepted")
                      : t("preferences.enterprise.invitation.pending")}
                  </TableCell>
                  {canInviteMembers && (
                    <TableCell align="right">
                      {!invitation.accepted && (
                        <>
                          <Tooltip title={t("preferences.enterprise.resendInvitation")}>
                            <IconButton color="primary" onClick={() => handleResendInvitation(invitation)}>
                              <MailIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={t("preferences.enterprise.cancelInvitation")}>
                            <IconButton color="error" onClick={() => handleCancelInvitation(invitation)}>
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* 招待リストのページネーションコントロール */}
        {invitationTotalPages > 1 && (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
            <Pagination
              count={invitationTotalPages}
              page={invitationCurrentPage}
              onChange={(event, value) => setInvitationCurrentPage(value)}
              color="primary"
            />
          </Box>
        )}
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        <PermissionMatrix />
      </TabPanel>

      {/* 招待ダイアログ */}
      <InviteDialog
        open={openInviteDialog}
        onClose={handleInviteClose}
        inviteEmail={inviteEmail}
        setInviteEmail={setInviteEmail}
        inviteRole={inviteRole}
        setInviteRole={setInviteRole}
        onInvite={handleInviteMember}
        canManageManagers={canManageManagers}
      />

      {/* メンバー削除確認ダイアログ */}
      <DeleteMemberDialog
        open={openDeleteDialog}
        onClose={handleDeleteMemberClose}
        onDelete={handleDeleteMember}
        selectedMember={selectedMember}
      />

      {/* メンバー編集ダイアログ */}
      <EditMemberDialog
        open={openEditRoleDialog}
        onClose={handleEditRoleClose}
        onUpdate={handleUpdateMember}
        selectedMember={selectedMember}
        editRole={editRole}
        setEditRole={setEditRole}
        canManageManagers={canManageManagers}
        currentUserId={user?.uuid}
      />
    </Box>
  );
};

export default MemberManagement;
